import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Eye, EyeOff, LogIn, Mail, Lock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleLogin } from '@react-oauth/google';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginForm = ({ onToggleMode }) => {
  const { login, verify2FALogin } = useAuth();
  const navigate = useNavigate();
  
  // Debug useAuth hook
  console.log('LoginForm mounted, login function available:', typeof login);
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  // 2FA challenge state
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');

  // Debug loading state changes
  console.log('LoginForm render - loading state:', loading);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    if (needsVerification) setNeedsVerification(false);
  };

  const handleResendVerification = async () => {
    if (!formData.email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    setResendingVerification(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification-public`, {
        email: formData.email
      });
      toast.success('Verification email sent! Please check your inbox.');
      setNeedsVerification(false);
      setError('');
    } catch (err) {
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('=== LOGIN FORM SUBMITTED ===');
    console.log('Form data:', { email: formData.email, hasPassword: !!formData.password });
    console.log('Login function type:', typeof login);
    
    if (!formData.email.trim() || !formData.password.trim()) {
      console.log('Validation failed: empty fields');
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    console.log('Calling login function...');
    
    try {
      const result = await login(formData.email, formData.password);
      console.log('Login result:', result);

      // 2FA required — switch to the TOTP code entry step
      if (result.requires2fa) {
        setTwoFAStep(true);
        setLoading(false);
        return;
      }

      if (result.success) {
        console.log('Login successful! Redirecting...');
        toast.success('Login successful!');
        
        // Get user data from storage to determine redirect
        const userDataStr = Cookies.get('enakid_user') || localStorage.getItem('enakid_user');
        if (userDataStr) {
          try {
            const userData = JSON.parse(userDataStr);
            
            // Admin always goes to dashboard
            if (userData.is_admin) {
              console.log('Admin login - redirecting to dashboard');
              setTimeout(() => navigate('/dashboard'), 1000);
              return;
            }
            
            // Check if user is affiliate by making a quick API call
            const token = Cookies.get('enakid_token');
            const affiliateCheck = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/affiliates/dashboard`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }).catch(() => null);
            
            if (affiliateCheck?.data?.affiliate) {
              // Check if they also have business plans
              const planCheck = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/business-plans`, {
                headers: { 'Authorization': `Bearer ${token}` }
              }).catch(() => ({ data: [] }));
              
              if (planCheck.data && planCheck.data.length > 0) {
                // Hybrid user - has both affiliate and business
                console.log('Hybrid user login - redirecting to dashboard (business priority)');
                setTimeout(() => navigate('/dashboard'), 1000);
              } else {
                // Affiliate-only user
                console.log('Affiliate-only user login - redirecting to affiliate dashboard');
                setTimeout(() => navigate('/affiliate'), 1000);
              }
            } else {
              // Regular business user
              console.log('Regular user login - redirecting to dashboard');
              setTimeout(() => navigate('/dashboard'), 1000);
            }
          } catch (parseError) {
            console.error('Error parsing user data:', parseError);
            setTimeout(() => navigate('/dashboard'), 1000);
          }
        } else {
          console.log('No user data found, default redirect to dashboard');
          setTimeout(() => navigate('/dashboard'), 1000);
        }
      } else {
        console.log('Login failed:', result.error);
        // Check if it's an email verification error
        if (result.error && result.error.toLowerCase().includes('verify')) {
          setNeedsVerification(true);
        }
        setError(result.error || 'Login failed. Please try again.');
        toast.error(result.error || 'Login failed');
      }
    } catch (err) {
      console.error('Login exception:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Login failed. Please try again.';
      // Check if it's an email verification error
      if (errorMessage.toLowerCase().includes('verify')) {
        setNeedsVerification(true);
      }
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      console.log('Login attempt complete, loading state reset');
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFACode.trim()) {
      setError('Please enter your authentication code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await verify2FALogin(formData.email, twoFACode.trim());
      if (result.success) {
        if (result.warning) toast.warning(result.warning);
        toast.success('Login successful!');
        const userDataStr = Cookies.get('enakid_user');
        let dest = '/dashboard';
        try {
          if (userDataStr && !JSON.parse(userDataStr).is_admin) dest = '/dashboard';
        } catch (_) {}
        setTimeout(() => navigate(dest), 800);
      } else {
        setError(result.error || 'Invalid code. Please try again.');
        toast.error(result.error || 'Invalid code');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Google Sign-In handler
  const handleGoogleSuccess = async (credentialResponse) => {
    console.log('Google Sign-In Success:', credentialResponse);
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/google`, {
        token: credentialResponse.credential
      });
      
      if (response.data.access_token) {
        // Store token and user data
        Cookies.set('enakid_token', response.data.access_token, { expires: 7 });
        Cookies.set('enakid_user', JSON.stringify(response.data.user), { expires: 7 });
        localStorage.setItem('enakid_token', response.data.access_token);
        localStorage.setItem('enakid_user', JSON.stringify(response.data.user));
        
        toast.success('Google login successful!');
        
        // Redirect based on user type
        if (response.data.user.is_admin) {
          setTimeout(() => navigate('/dashboard'), 500);
        } else {
          // Check affiliate status
          const affiliateCheck = await axios.get(`${BACKEND_URL}/api/affiliates/dashboard`, {
            headers: { 'Authorization': `Bearer ${response.data.access_token}` }
          }).catch(() => null);
          
          if (affiliateCheck?.data?.affiliate) {
            const planCheck = await axios.get(`${BACKEND_URL}/api/business-plans`, {
              headers: { 'Authorization': `Bearer ${response.data.access_token}` }
            }).catch(() => ({ data: [] }));
            
            if (planCheck.data && planCheck.data.length > 0) {
              setTimeout(() => navigate('/dashboard'), 500);
            } else {
              setTimeout(() => navigate('/affiliate'), 500);
            }
          } else {
            setTimeout(() => navigate('/dashboard'), 500);
          }
        }
        
        // Trigger auth context update
        window.location.reload();
      }
    } catch (err) {
      console.error('Google login error:', err);
      const errorMessage = err.response?.data?.detail || 'Google login failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.log('Google Sign-In Error');
    toast.error('Google login failed. Please try again.');
  };

  // Simple direct login for testing
  const directLogin = async () => {
    console.log('=== DIRECT LOGIN FUNCTION ===');
    setError('');
    setLoading(true);
    
    try {
      const result = await login('demo@enakid.test', 'demo123456');
      console.log('Direct login result:', result);
      
      if (result.success) {
        console.log('✅ Login successful!');
        toast.success('Login successful!');
        // Force redirect to dashboard
        window.location.href = '/dashboard';
      } else {
        console.log('❌ Login failed:', result.error);
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('Direct login error:', error);
      setError('Login failed. Please try again.');
    }
    
    setLoading(false);
  };

  if (twoFAStep) {
    return (
      <Card className="w-full max-w-md glass-card shadow-strong" data-testid="login-2fa-card">
        <CardHeader className="text-center">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-4 w-16 h-16 mx-auto mb-4">
            <Lock className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">Two-Factor Authentication</CardTitle>
          <CardDescription>
            Enter the 6-digit code from your authenticator app, or a backup code.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleVerify2FA} className="space-y-4">
            {error && (
              <Alert variant="destructive" data-testid="login-2fa-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="twofa-code">Authentication Code</Label>
              <Input
                id="twofa-code"
                data-testid="login-2fa-code-input"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                placeholder="123456"
                value={twoFACode}
                onChange={(e) => { setTwoFACode(e.target.value); if (error) setError(''); }}
                className="text-center tracking-[0.4em] text-lg form-input"
                disabled={loading}
                autoFocus
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="login-2fa-submit-button">
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Verifying...
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-2" />
                  Verify &amp; Sign In
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              disabled={loading}
              data-testid="login-2fa-back-button"
              onClick={() => { setTwoFAStep(false); setTwoFACode(''); setError(''); }}
            >
              Back to login
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="w-full max-w-md border border-slate-200/80 bg-white shadow-[0_20px_60px_-15px_rgba(15,23,42,0.15)] rounded-3xl"
      data-testid="login-card"
    >
      <CardHeader className="text-center space-y-3 pb-2">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 shadow-lg shadow-blue-600/25">
          <LogIn className="h-7 w-7 text-white" />
        </div>
        <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">
          Welcome back
        </CardTitle>
        <CardDescription className="text-slate-500">
          Sign in to your TavPlans account to keep building your financial models
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          {error && (
            <Alert variant="destructive" data-testid="login-error">
              <AlertDescription>
                {error}
                {needsVerification && (
                  <div className="mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      className="w-full bg-white hover:bg-gray-50"
                    >
                      {resendingVerification ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4 mr-2" />
                          Resend Verification Email
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="h-12 pl-11 rounded-xl border-slate-200 bg-slate-50/50 focus-visible:ring-blue-500/30 focus-visible:border-blue-500"
                disabled={loading}
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <a
                href="/forgot-password"
                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </a>
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="h-12 pl-11 pr-11 rounded-xl border-slate-200 bg-slate-50/50 focus-visible:ring-blue-500/30 focus-visible:border-blue-500"
                disabled={loading}
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-blue-800 text-base font-semibold shadow-lg shadow-blue-600/25 transition-all duration-200 hover:shadow-xl hover:shadow-blue-600/30 hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-70"
            data-testid="login-submit-button"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Signing In...
              </>
            ) : (
              <>
                <LogIn className="h-4 w-4 mr-2" />
                Sign In
              </>
            )}
          </Button>
        </form>

        {/* Social Login Options */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                theme="outline"
                size="large"
                text="signin_with"
                shape="rectangular"
                logo_alignment="left"
              />
            </div>

            <button
              type="button"
              onClick={() => {
                // TODO: Implement Telegram OAuth
                alert('Telegram authentication coming soon!');
              }}
              className="w-full inline-flex justify-center items-center py-2 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#0088cc">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
              </svg>
              Telegram
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-slate-600">
            Don't have an account?{' '}
            <button
              onClick={() => window.location.href = '/signup'}
              className="font-semibold text-blue-600 hover:text-blue-800 hover:underline"
              data-testid="create-account-link"
            >
              Create Account
            </button>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default LoginForm;